'use client'

import { ColumnDef } from '@tanstack/react-table'
import { UnifiedUserLocationRow } from '../types'
import { CustomerSubAccount } from '../../CustomerSubAccounts/types'
import { CustomerAccountActions } from './CustomerAccountActions'
import { CustomerSubAccountActions } from '../../CustomerSubAccounts/components/CustomerSubAccountActions'
import { Badge } from '@/components/ui/badge'
import { Building2, Crown, Mail, User, Users } from 'lucide-react'
import { useMutation } from '@apollo/client'
import { CUSTOMER_ACCOUNT_SEND_INVITATION } from '../customerAccountQueries'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'

const getStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    NEW: 'New',
    INVITE_SENT: 'Invite Sent',
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    SUSPENDED: 'Suspended',
  }
  return statusLabels[status] || status
}

const getStatusVariant = (
  status: string
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'NEW':
      return 'secondary'
    case 'INVITE_SENT':
      return 'outline'
    case 'ACTIVE':
      return 'default'
    case 'INACTIVE':
      return 'secondary'
    case 'SUSPENDED':
      return 'destructive'
    default:
      return 'default'
  }
}

export const createUnifiedColumns = (
  customerId: number,
  onEditLocation: (location: CustomerSubAccount) => void,
  onEditAccount: (account: any) => void,
  onAddUserToLocation?: (location: CustomerSubAccount) => void
): ColumnDef<UnifiedUserLocationRow>[] => [
  {
    accessorKey: 'email',
    header: 'User / Location',
    cell: ({ row }) => {
      const data = row.original

      if (data.type === 'location-header') {
        return (
          <div className="flex items-center gap-2 py-2 font-semibold text-blue-700  ">
            <Building2 className="h-4 w-4" />
            <span>{data.locationName || 'Unnamed Location'}</span>
            <Badge variant="lightBlue" className="ml-2">
              <Users className="h-3 w-3 mr-1" />
              {data.locationUserCount} users
            </Badge>
          </div>
        )
      }

      if (data.type === 'super-user') {
        return (
          <div className="flex items-center gap-2 font-medium">
            <Crown className="h-4 w-4 text-amber-500" />
            <span>{data.email}</span>
          </div>
        )
      }

      // location-user
      return (
        <div className="pl-6 font-medium flex items-center gap-2">
          {' '}
          <User className="h-4 w-4" />
          {data.email}
        </div>
      )
    },
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const data = row.original

      if (data.type === 'location-header') {
        return null
      }

      return <div>{data.name || '-'}</div>
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const data = row.original

      // Don't show status for location headers
      if (data.type === 'location-header') {
        return null
      }

      const status = row.getValue('status') as string
      const [sendInvitation, { loading }] = useMutation(
        CUSTOMER_ACCOUNT_SEND_INVITATION
      )

      const handleSendInvitation = async () => {
        try {
          await sendInvitation({
            variables: { id: row.original.id },
          })
          toast.success('Invitation sent successfully')
        } catch (error) {
          console.error('Failed to send invitation:', error)
          toast.error('Failed to send invitation')
        }
      }

      return (
        <div className="flex items-center gap-2">
          <Badge
            variant={getStatusVariant(status || 'ACTIVE')}
            className="whitespace-nowrap"
          >
            {getStatusLabel(status || 'ACTIVE')}
          </Badge>
          {status === 'NEW' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleSendInvitation}
              disabled={loading}
              className="h-6 w-6 p-0"
            >
              <Mail className="h-3 w-3" />
            </Button>
          )}
        </div>
      )
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const data = row.original
      const [sendInvitation, { loading }] = useMutation(
        CUSTOMER_ACCOUNT_SEND_INVITATION
      )

      const handleResendInvite = async () => {
        try {
          await sendInvitation({
            variables: { id: data.id },
          })
          toast.success('Invitation resent successfully')
        } catch (error) {
          console.error('Failed to resend invitation:', error)
          toast.error('Failed to resend invitation')
        }
      }

      if (data.type === 'location-header' && data.originalLocation) {
        // Convert originalLocation to CustomerSubAccount format
        const customerSubAccount = {
          id: data.originalLocation.id,
          name: data.originalLocation.name,
          createdAt: data.originalLocation.createdAt,
          updatedAt: data.originalLocation.createdAt, // Using createdAt as fallback
          customerId: customerId,
          _count: data.originalLocation._count,
        }

        return (
          <CustomerSubAccountActions
            customerSubAccount={customerSubAccount}
            onEdit={onEditLocation}
            onAddUser={onAddUserToLocation}
          />
        )
      }

      if (data.originalAccount) {
        return (
          <CustomerAccountActions
            customerAccount={data.originalAccount}
            onEdit={onEditAccount}
          />
        )
      }

      return null
    },
  },
]
